import { computed, reactive, ref } from "vue";

import type { CompanyProfile, CompanyProfileInput } from "@/entities/company";
import { companyApi } from "@/entities/company";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib/toast";

import type { CompanyErrorField, CompanyErrorKey } from "./mapCompanyError";
import { mapCompanyError } from "./mapCompanyError";

export type SubmitStatus = "idle" | "saving";
export type FieldKey = "name" | "address" | "phone" | "description";
export type FieldErrors = Partial<Record<FieldKey | "form", CompanyErrorKey>>;

export interface UseSalonProfileFormOptions {
  onSaved: (updated: CompanyProfile) => void;
}

const NAME_MAX = 255;
const ADDRESS_MAX = 255;
const PHONE_MAX = 32;
const DESCRIPTION_MAX = 1000;

const FIELD_ORDER: FieldKey[] = ["name", "address", "phone", "description"];

function tr(key: string): string {
  return i18n.global.t(key);
}

function emptyInput(): CompanyProfileInput {
  return { name: "", address: "", phone: "", description: "" };
}

/**
 * Composable форми профілю салону (spec §3.2). Seed із prop (host — єдине джерело
 * company; форма самостійно не завантажує). Full-replace: усі 4 поля, "" = clear.
 */
export function useSalonProfileForm(opts: UseSalonProfileFormOptions) {
  const submitStatus = ref<SubmitStatus>("idle");
  const fields = reactive<CompanyProfileInput>(emptyInput());
  const fieldErrors = reactive<FieldErrors>({});
  const firstInvalidField = ref<FieldKey | null>(null);

  const descriptionLength = computed(() => [...fields.description].length);

  function clearErrors(): void {
    for (const k of Object.keys(fieldErrors) as (keyof FieldErrors)[]) {
      delete fieldErrors[k];
    }
    firstInvalidField.value = null;
  }

  function seedFrom(c: CompanyProfile): void {
    fields.name = c.name;
    fields.address = c.address;
    fields.phone = c.phone;
    fields.description = c.description;
    clearErrors();
  }

  /**
   * Client-side валідація (UX-guard; сервер авторитетний). name required ≤255;
   * address ≤255; phone ≤32; description ≤1000 рун ([...v].length).
   */
  function validate(): boolean {
    clearErrors();
    const name = fields.name.trim();
    const address = fields.address.trim();
    const phone = fields.phone.trim();
    // description: trim не застосовуємо для підрахунку рун (user може хотіти пробіли)
    const descRunes = [...fields.description].length;

    if (!name) {
      fieldErrors.name = "salon.profile.errors.nameRequired";
    } else if (name.length > NAME_MAX) {
      fieldErrors.name = "salon.profile.errors.nameInvalid";
    }
    if (address.length > ADDRESS_MAX) {
      fieldErrors.address = "salon.profile.errors.addressTooLong";
    }
    if (phone.length > PHONE_MAX) {
      fieldErrors.phone = "salon.profile.errors.phoneTooLong";
    }
    if (descRunes > DESCRIPTION_MAX) {
      fieldErrors.description = "salon.profile.errors.descriptionTooLong";
    }

    firstInvalidField.value = FIELD_ORDER.find((f) => fieldErrors[f]) ?? null;
    return firstInvalidField.value === null;
  }

  function applyServerError(e: AppError): void {
    const mapped = mapCompanyError(e);
    switch (mapped.surface) {
      case "validation":
        if (mapped.field) {
          fieldErrors[mapped.field as CompanyErrorField] = mapped.i18nKey;
          firstInvalidField.value = mapped.field as FieldKey;
        } else {
          fieldErrors.form = mapped.i18nKey;
        }
        break;
      case "server":
      case "network":
        fieldErrors.form = mapped.i18nKey;
        break;
      case "notFound":
        toast.error(tr(mapped.i18nKey));
        break;
      default:
        // forbidden / unknown → toast
        toast.error(tr(mapped.i18nKey));
    }
  }

  /**
   * Сабміт форми. Guard подвійного сабміту → validate → saving →
   * updateCompany → onSaved + toast + re-seed. Помилка → applyServerError.
   */
  async function submit(): Promise<boolean> {
    if (submitStatus.value === "saving") {
      return false;
    }
    if (!validate()) {
      return false;
    }
    submitStatus.value = "saving";
    try {
      const input: CompanyProfileInput = {
        name: fields.name.trim(),
        address: fields.address.trim(),
        phone: fields.phone.trim(),
        description: fields.description.trim(),
      };
      const updated = await companyApi.updateCompany(input);
      opts.onSaved(updated);
      toast.success(tr("salon.profile.saved"));
      seedFrom(updated);
      return true;
    } catch (e) {
      applyServerError(e as AppError);
      return false;
    } finally {
      submitStatus.value = "idle";
    }
  }

  return {
    fields,
    fieldErrors,
    firstInvalidField,
    submitStatus,
    descriptionLength,
    seedFrom,
    validate,
    submit,
  };
}

export type SalonProfileForm = ReturnType<typeof useSalonProfileForm>;
